import { useCallback } from "react";
import { api, ApiError } from "../../../lib/api";
import { queryClient } from "../../../app/queryClient";
import { useAuth } from "../../../providers/AuthProvider";

export type ProductPricing = {
  productId: string;
  unit_price: number;
  mrp: number | null;
  // For "1 batch active until exhausted", this is the active batch remaining (falls back to net if needed).
  available_qty: number;
  available_qty_net: number;
  reserved_qty_total: number;
  your_reserved_qty: number;
  pricing_source: string;
  active_batch_id: string | null;
};

function parseMoney(value: string): number {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) throw new Error("Invalid price from server");
  return n;
}

function parseQty(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) throw new Error("Invalid quantity from server");
  return n;
}

function normalizePricing(productId: string, res: {
  unit_sell_price: string | null;
  unit_mrp: string | null;
  available_qty_net: string;
  reserved_qty_total: string;
  your_reserved_qty: string;
  pricing_source: string;
  active_batch: null | { id: string; received_at: string | null; quantity_remaining: string };
}): ProductPricing {
  if (res.unit_sell_price === null) {
    throw new ApiError({ code: "PRICE_NOT_AVAILABLE", message: "Price not available for this product" });
  }

  return {
    productId,
    unit_price: parseMoney(res.unit_sell_price),
    mrp: res.unit_mrp === null ? null : parseMoney(res.unit_mrp),
    available_qty: parseQty(res.active_batch?.quantity_remaining ?? res.available_qty_net),
    available_qty_net: parseQty(res.available_qty_net),
    reserved_qty_total: parseQty(res.reserved_qty_total),
    your_reserved_qty: parseQty(res.your_reserved_qty),
    pricing_source: res.pricing_source,
    active_batch_id: res.active_batch?.id ?? null,
  };
}

export function useProductPricing() {
  const { token } = useAuth();

  const clearCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["posPricing"] });
  }, []);

  const getPricing = useCallback(async (productId: string | number): Promise<ProductPricing> => {
    if (!token) throw new ApiError({ code: "UNAUTHENTICATED", message: "Not authenticated" });
    const key = String(productId);

    return queryClient.fetchQuery({
      queryKey: ["posPricing", key],
      staleTime: 45_000,
      queryFn: async () => {
        const res = await api.posPricing.getProductPricing({ token, productId: key });
        return normalizePricing(key, res);
      },
    });
  }, [token]);

  const prefetchPricing = useCallback(async (productIds: Array<string | number>) => {
    if (!token) throw new ApiError({ code: "UNAUTHENTICATED", message: "Not authenticated" });
    const ids = Array.from(new Set(productIds.map((v) => String(v)))).filter((v) => v !== "");
    if (ids.length === 0) return {} as Record<string, ProductPricing>;

    const res = await api.posPricing.getBatchPricing({ token, ids });
    const out: Record<string, ProductPricing> = {};
    for (const [productId, dto] of Object.entries(res.data ?? {})) {
      try {
        const normalized = normalizePricing(productId, dto);
        out[productId] = normalized;
        queryClient.setQueryData(["posPricing", productId], normalized);
      } catch {
        // Skip invalid/price-not-available products; callers handle missing.
      }
    }
    return out;
  }, [token]);

  return { getPricing, prefetchPricing, clearCache };
}

